import React, { useEffect, useState } from 'react';
import { Col, CustomInput, FormGroup, Label, Row } from 'reactstrap';
import SearchInput from '../../../commons/SearchInput';
import { ContFilterYear, ContPaginationPage } from '../../../styled-components';
import generatePaginationOptions from '../../../lib/helpers/generatePaginationOptions';
import { useAuth } from '../../../commons/Auth';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'react-alert';
import { useLoading } from '../../../commons/Loading';
import {
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import axios from 'axios';
import AgreementsTable from './AgreementsTable';
import NewExternalAgreementModal from './NewExternalAgreementModal';
import { ADMIN_TYPES } from '../../../App';

const { REACT_APP_DEFAULT_PAGINATION_LIMIT = 50 } = process.env;

const SupplierAdminPresentational = ({
  activities,
  suppliers,
  fetchSuppliers,
  handleDelete,
  handleFileListView,
}) => {
  const [{ newToken, profile = {} } = {}] = useAuth();
  const [t] = useTranslation('EXTERNAL_AGREEMENTS');
  const alert = useAlert();
  const { showLoading, hideLoading } = useLoading();

  const [externalAgreements, setExternalAgreements] = useState({});
  const [total, setTotal] = useState(0);

  const [query, setQuery] = useQueryParams({
    limit: withDefault(NumberParam, +REACT_APP_DEFAULT_PAGINATION_LIMIT),
    offset: NumberParam,
    name: withDefault(StringParam, ''),
    admin: withDefault(StringParam, ''),
    supplier: withDefault(StringParam, ''),
    vatCode: withDefault(StringParam, ''),
    isExternalSupplier: withDefault(StringParam, ''),
  });

  const { limit, offset, name, admin, supplier, vatCode, isExternalSupplier } =
    query;

  const fetchExternalAgreements = async () => {
    showLoading();

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/external-agreements/browse`,
      {
        ...query,
        agreementType: 'SUPPLIER_ADMIN',
      },
      {
        headers: { Authorization: newToken },
      }
    );

    hideLoading();

    if (response instanceof Error) {
      return alert.error(t('COMMON:GENERIC_ERROR'));
    }

    const { data = [], headers = {} } = response;

    setExternalAgreements(data.externalAgreements);
    setTotal(+headers['x-total-count']);
  };

  function onFilter(queryDiff) {
    if (
      (queryDiff.name && queryDiff.name !== name) || // first search
      (name && queryDiff.name === '') || // search deleted
      (queryDiff.admin && queryDiff.admin !== admin) || // first search
      (admin && queryDiff.admin === '') || // search deleted
      (queryDiff.supplier && queryDiff.supplier !== supplier) || // first search
      (supplier && queryDiff.supplier === '') || // search deleted
      (queryDiff.vatCode && queryDiff.vatCode !== vatCode) || // first search
      (vatCode && queryDiff.vatCode === '') || // search deleted
      (queryDiff.isExternalSupplier &&
        queryDiff.isExternalSupplier !== isExternalSupplier) || // first search
      (isExternalSupplier && queryDiff.isExternalSupplier === '') || // search deleted
      (queryDiff.limit && queryDiff.limit !== limit)
    ) {
      queryDiff.offset = 0; // return to first page
    }

    setQuery(queryDiff);
  }

  const uploadExternalAgreements = async (data, toggle) => {
    showLoading();

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/external-agreements`,
      data,
      {
        headers: { Authorization: newToken },
      }
    );
    hideLoading();

    if (response instanceof Error) {
      return alert.error(t('COMMON:GENERIC_ERROR'));
    }

    fetchExternalAgreements();
    toggle();
    alert.success(t('COMMON:SUCCESS_UPLOAD'));
  };

  useEffect(() => {
    fetchExternalAgreements();
  }, [limit, offset, name, admin, supplier, vatCode, isExternalSupplier]);

  return (
    <>
      {[...ADMIN_TYPES, 'ADMIN_COLLAB'].includes(profile.type) && (
        <Row>
          <Col sm="12" md="12" lg="6" xl="4" className="ml-auto">
            <NewExternalAgreementModal
              profile={profile}
              activities={activities}
              suppliers={suppliers}
              fetchSuppliers={fetchSuppliers}
              onConfirm={uploadExternalAgreements}
            />
          </Col>
        </Row>
      )}

      <Row className="search-add-cont m-t-20 d-flex" style={{ rowGap: '10px' }}>
        {![...ADMIN_TYPES, 'ADMIN_COLLAB'].includes(profile.type) && (
          <Col sm="12" md="6" lg="3" xl="3">
            <SearchInput
              query={{ search: query && query.admin }}
              onFilter={({ search }) => onFilter({ admin: search })}
              label={t('SEARCH:BY_ADMIN')}
            />
          </Col>
        )}

        {[...ADMIN_TYPES, 'ADMIN_COLLAB'].includes(profile.type) && (
          <Col sm="12" md="6" lg="3" xl="3">
            <SearchInput
              query={{ search: query && query.supplier }}
              onFilter={({ search }) => onFilter({ supplier: search })}
              label={t('SEARCH:BY_SUPPLIER')}
            />
          </Col>
        )}

        <Col sm="12" md="6" lg="3" xl="3">
          <SearchInput
            query={{ search: query && query.name }}
            onFilter={({ search }) => onFilter({ name: search })}
            label={t('SEARCH:NAME_AGREEMENT')}
          />
        </Col>

        {[...ADMIN_TYPES, 'ADMIN_COLLAB'].includes(profile.type) && (
          <Col sm="12" md="6" lg="3" className="select-wrapper">
            <ContFilterYear className="no-width">
              <Row>
                <Col xs="6">
                  <Label>Tipo fornitore</Label>
                </Col>
                <Col xs="6">
                  <FormGroup>
                    <CustomInput
                      type="select"
                      id="isExternalSupplier"
                      name="isExternalSupplier"
                      onChange={(e) =>
                        onFilter({
                          isExternalSupplier: e.target.value,
                        })
                      }
                      value={isExternalSupplier || ''}
                    >
                      <option value="" defaultChecked>
                        Tutti
                      </option>
                      <option value="EXTERNAL">Esterno</option>
                      <option value="INTERNAL">Interno</option>
                    </CustomInput>
                  </FormGroup>
                </Col>
              </Row>
            </ContFilterYear>
          </Col>
        )}
      </Row>

      <section style={{ marginTop: '10px' }}>
        <AgreementsTable
          data={externalAgreements}
          total={total}
          offset={offset}
          limit={limit}
          onFilter={onFilter}
          profile={profile}
          handleFileListView={handleFileListView}
          handleDelete={handleDelete}
          fetchExternalAgreements={fetchExternalAgreements}
        />

        <ContPaginationPage>
          <p>{t('COMMON:PAGINATION_PER_PAGE')}</p>

          <FormGroup>
            <CustomInput
              type="select"
              className="pagination-select"
              id="perPage"
              name="perPage"
              value={query.limit}
              onChange={({ target: { value: limit } }) => {
                onFilter({ limit });
              }}
            >
              {generatePaginationOptions()}
            </CustomInput>
          </FormGroup>
        </ContPaginationPage>
      </section>
    </>
  );
};

export default SupplierAdminPresentational;
